import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { IonIcon } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import {
  chevronDownOutline, closeOutline,
  chevronForwardOutline, rocketOutline,
} from 'ionicons/icons';
import { Subscription, filter } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { HubsHttpService } from '../../services/api/hubs-http.service';
import { GuideWidgetStateService } from '../../services/guide-widget-state.service';
import { OrgBillingService } from '../../services/api/org-billing.service';
import { TourService } from '../../services/tour.service';

export interface SetupStep {
  key: 'total_hubs' | 'total_fleets' | 'total_drivers' | 'total_kids' | 'total_kids_linked_to_hub' | 'total_trips';
  label: string;
  route: string;
}

export const SETUP_STEPS: SetupStep[] = [
  { key: 'total_hubs', label: 'Add Hub', route: '/hubs/add' },
  { key: 'total_fleets', label: 'Add Fleet', route: '/fleets/add' },
  { key: 'total_drivers', label: 'Add Driver', route: '/drivers/add' },
  { key: 'total_kids', label: 'Add Kids', route: '/kids/add' },
  // A kid has to be linked to a hub (pickup/drop, "Add" under the Pickup
  // & Drop card on Kid Detail) before they can go into a route or get a
  // billing plan — this step was previously skipped straight past.
  { key: 'total_kids_linked_to_hub', label: 'Add Pickup & Drop', route: '/kids/list' },
  { key: 'total_trips', label: 'Add Route', route: '/trips/add' },
];

// sessionStorage (not localStorage) so a dismiss only lasts this app
// session — closing and reopening the app brings it back if setup is
// still incomplete, matching Stripe's own "closable but comes back" widget.
// Scoped per org: without the org id in the key, dismissing while testing
// one org would incorrectly stay dismissed after switching to another org
// in the same browser tab/session.
const DISMISS_KEY_PREFIX = 'h2s_setup_guide_dismissed_';

@Component({
  selector: 'app-setup-guide-widget',
  standalone: true,
  imports: [CommonModule, IonIcon],
  templateUrl: './setup-guide-widget.component.html',
  styleUrls: ['./setup-guide-widget.component.scss'],
})
export class SetupGuideWidgetComponent implements OnInit, OnDestroy {
  steps = SETUP_STEPS;
  kpis: any = null;
  collapsed = false;
  dismissed = false;

  /** True right after the last step completes, so the "all done" card gets
   *  one render before the widget stops showing on future loads. */
  justCompleted = false;

  /** True while the Payments widget is the one occupying the single
   *  bottom-right slot — starting that guide hides this one. */
  suppressedByOtherGuide = false;

  /** Org's own Hop2School subscription tier (not orgs.is_premium, which is
   *  an unrelated trial feature-gate) — only paid-tier orgs can use
   *  /payments at all (requirePaidTier middleware), so the "keep going"
   *  nudge toward Payments only makes sense for them. */
  isPaidPlan = false;

  /** True while a driver.js hint is showing — hides the widget so it
   *  doesn't sit on top of (and obscure) the element being pointed at. */
  hintActive = false;

  private subs: Subscription[] = [];
  private completingSetup = false;
  private lastDoneCount = 0;

  constructor(
    private auth: AuthService,
    private http: HubsHttpService,
    private router: Router,
    private guideState: GuideWidgetStateService,
    private orgBilling: OrgBillingService,
    private tourService: TourService,
  ) {
    addIcons({ chevronDownOutline, closeOutline, chevronForwardOutline, rocketOutline });
  }

  ngOnInit(): void {
    this.subs.push(
      this.auth.currentUser$.subscribe(user => {
        this.kpis = null;
        this.justCompleted = false;
        this.dismissed = user ? sessionStorage.getItem(this.dismissKey(user.orgId)) === 'true' : false;
        if (user && this.auth.canManage()) this.load();
      }),
    );

    // Refresh after navigation so completing a step elsewhere (e.g. adding
    // a hub) is reflected here without a manual reload.
    this.subs.push(
      this.router.events
        .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
        .subscribe(() => {
          if (this.auth.getCurrentUser() && this.auth.canManage()) this.load();
        }),
    );

    this.subs.push(
      this.guideState.activeGuide$.subscribe(guide => {
        this.suppressedByOtherGuide = guide === 'payments';
        if (guide === 'setup') {
          // Explicit "Start" press — show even if dismissed earlier.
          const orgId = this.auth.getCurrentUser()?.orgId;
          if (orgId) sessionStorage.removeItem(this.dismissKey(orgId));
          this.dismissed = false;
          this.collapsed = false;
        }
      }),
    );

    this.subs.push(
      this.tourService.active$.subscribe(active => { this.hintActive = active; }),
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }

  private load(): void {
    this.http.getDashboardKPIs().subscribe({
      next: (kpis) => {
        this.kpis = kpis;
        const newDoneCount = this.doneCount;
        // A step finished while minimized — re-surface the card so the
        // completed check and the next step are actually seen, instead of
        // silently updating behind a collapsed dock.
        if (this.collapsed && newDoneCount > this.lastDoneCount) {
          this.collapsed = false;
        }
        this.lastDoneCount = newDoneCount;
        if (!kpis.setup_completed_at && this.doneCount === this.steps.length && !this.completingSetup) {
          this.completingSetup = true;
          this.http.completeSetup().subscribe({
            next: () => {
              this.kpis = { ...this.kpis, setup_completed_at: new Date().toISOString() };
              this.justCompleted = true;
              this.orgBilling.getStatus().subscribe({
                next: (status) => { this.isPaidPlan = status.status === 'active'; },
                error: () => {},
              });
            },
            error: () => { this.completingSetup = false; },
          });
        }
      },
      error: () => { /* silent — widget just stays hidden */ },
    });
  }

  get visible(): boolean {
    if (!this.kpis || this.dismissed || this.suppressedByOtherGuide || this.hintActive) return false;
    return !this.kpis.setup_completed_at || this.justCompleted;
  }

  get doneCount(): number {
    return this.steps.filter(s => this.isDone(s)).length;
  }

  isDone(step: SetupStep): boolean {
    return !!this.kpis && this.kpis[step.key] > 0;
  }

  onStepClick(step: SetupStep): void {
    this.guideState.navigateForGuide(this.router, 'setup', step.route, {
      pickupDropHint: step.key === 'total_kids_linked_to_hub',
    });
  }

  toggleCollapse(): void {
    this.collapsed = !this.collapsed;
  }

  startPaymentsGuide(): void {
    this.guideState.showPaymentsGuide();
  }

  dismiss(event: Event): void {
    event.stopPropagation();
    this.dismissed = true;
    const orgId = this.auth.getCurrentUser()?.orgId;
    if (orgId) sessionStorage.setItem(this.dismissKey(orgId), 'true');
    this.guideState.hideGuide('setup');

    // Only exists on Dashboard's header — no-ops elsewhere rather than
    // forcing a navigation just to show this reminder.
    const helpIcon = document.querySelector<HTMLElement>('.help-icon-btn');
    if (helpIcon) {
      this.tourService.showHint([{
        element: helpIcon,
        popover: {
          title: 'Need this again?',
          description: 'You can access the setup guide anytime using the help icon up here.',
        },
      }]);
    }
  }

  private dismissKey(orgId: string): string {
    return `${DISMISS_KEY_PREFIX}${orgId}`;
  }
}
